import {
  createContext, useContext, useEffect, useMemo, useState, type ReactNode
} from 'react';
import {
  onAuthStateChanged, signInWithPopup, signInWithRedirect, getRedirectResult,
  signOut as fbSignOut, type User
} from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db, googleProvider, isConfigured } from './firebase';
import { identify, track } from './analytics';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState>({
  user: null, loading: true, error: null,
  signIn: async () => {}, signOut: async () => {}
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isConfigured) { setLoading(false); return; }
    // A redirect sign-in lands back here; surface its error rather than
    // silently showing the sign-in button again.
    getRedirectResult(auth).catch((e) => setError(readableAuthError(e)));
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
      identify(u?.uid ?? null);
      if (u) {
        void setDoc(
          doc(db, 'users', u.uid),
          {
            displayName: u.displayName || 'Someone',
            email: u.email || null,
            photoURL: u.photoURL || null,
            lastSeenAt: serverTimestamp()
          },
          { merge: true }
        ).catch(() => { /* profile is a nicety, never block sign-in on it */ });
      }
    });
  }, []);

  const value = useMemo<AuthState>(() => ({
    user, loading, error,
    async signIn() {
      setError(null);
      try {
        await signInWithPopup(auth, googleProvider);
        track('login', { method: 'google' });
      } catch (e) {
        const code = (e as { code?: string }).code || '';
        // Popups are blocked in some in-app browsers; redirect still works there.
        if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment') {
          await signInWithRedirect(auth, googleProvider);
          return;
        }
        if (code === 'auth/cancelled-popup-request' || code === 'auth/popup-closed-by-user') return;
        setError(readableAuthError(e));
      }
    },
    async signOut() { await fbSignOut(auth); }
  }), [user, loading, error]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);

function readableAuthError(e: unknown): string {
  const code = (e as { code?: string }).code || '';
  if (code === 'auth/unauthorized-domain')
    return 'This domain is not in the Firebase authorised list. Add it under Authentication → Settings → Authorised domains.';
  if (code === 'auth/network-request-failed')
    return 'Could not reach Firebase. Check your connection and try again.';
  if (code === 'auth/invalid-api-key' || code === 'auth/api-key-not-valid')
    return 'The Firebase API key in .env.local is not valid for this project.';
  return (e as { message?: string }).message || 'Sign-in failed. Try again.';
}
